import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Phone, Lock, LogOut, Download, Home, FileText, Loader2, CheckCircle2, AlertTriangle, Smartphone, X } from "lucide-react";
import {
  tenantLogin, tenantRegister, tenantMe, tenantReceipts, tenantReceiptUrl,
  getTenantToken, setTenantToken, clearTenantToken,
} from "../lib/tenantApi";

const ACCENT = "#B45309";
const inputCls =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-navy placeholder-muted-foreground/60 outline-none focus:border-navy/40";

const fmt = (n) => (Number(n) || 0).toLocaleString("fr-FR");

export default function TenantSpace() {
  const [checking, setChecking] = useState(true);
  const [property, setProperty] = useState(null);
  const [receipts, setReceipts] = useState([]);

  const loadPortal = async () => {
    try {
      const me = await tenantMe();
      setProperty(me);
      const rc = await tenantReceipts();
      setReceipts(rc);
    } catch {
      clearTenantToken();
      setProperty(null);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (getTenantToken()) loadPortal();
    else setChecking(false);
  }, []);

  const onLoggedIn = async (token) => {
    setTenantToken(token);
    setChecking(true);
    await loadPortal();
  };

  const logout = () => {
    clearTenantToken();
    setProperty(null);
    setReceipts([]);
    toast.success("Déconnecté");
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-muted/40">
        <Loader2 className="animate-spin text-navy" />
      </main>
    );
  }

  return (
    <main data-testid="tenant-space" className="min-h-screen bg-gradient-to-b from-navy to-navy/95 py-16 px-5">
      <Link to="/" data-testid="tenant-close" aria-label="Fermer et revenir au site"
        className="fixed right-4 top-4 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20">
        <X size={20} />
      </Link>
      <div className="mx-auto max-w-3xl">
        {!property ? (
          <LoginCard onLoggedIn={onLoggedIn} />
        ) : (
          <Portal property={property} receipts={receipts} onLogout={logout} />
        )}
      </div>
    </main>
  );
}

function LoginCard({ onLoggedIn }) {
  const [screen, setScreen] = useState("login"); // "login" | "register"
  const [mode, setMode] = useState("code"); // "code" | "creds"
  const [code, setCode] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = mode === "code" ? { code } : { identifier, password };
      const { token } = await tenantLogin(body);
      toast.success("Bienvenue !");
      await onLoggedIn(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  if (screen === "register") return <RegisterCard onBack={() => setScreen("login")} />;

  return (
    <div className="mx-auto max-w-md rounded-3xl bg-white p-7 shadow-2xl">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: ACCENT }}>
          <Home size={22} className="text-white" />
        </div>
        <h1 className="font-display text-2xl font-semibold text-navy">Espace locataire</h1>
        <p className="mt-1 text-sm text-muted-foreground">Consultez et téléchargez vos reçus de loyer</p>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
        <button data-testid="tenant-tab-code" onClick={() => setMode("code")}
          className={`rounded-lg py-2 text-sm font-medium transition-colors ${mode === "code" ? "bg-white text-navy shadow-sm" : "text-muted-foreground"}`}>
          Code d'accès
        </button>
        <button data-testid="tenant-tab-creds" onClick={() => setMode("creds")}
          className={`rounded-lg py-2 text-sm font-medium transition-colors ${mode === "creds" ? "bg-white text-navy shadow-sm" : "text-muted-foreground"}`}>
          Téléphone / Email
        </button>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {mode === "code" ? (
          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground"><KeyRound size={13} /> Votre code d'accès</span>
            <input data-testid="tenant-login-code" value={code} onChange={(e) => setCode(e.target.value)}
              className={inputCls} placeholder="TIRERA-XXXXX" required />
          </label>
        ) : (
          <>
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Phone size={13} /> Téléphone ou email</span>
              <input data-testid="tenant-login-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                className={inputCls} placeholder="92 00 10 10 ou email@..." required />
            </label>
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock size={13} /> Mot de passe</span>
              <input data-testid="tenant-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls} placeholder="••••••••" required />
            </label>
          </>
        )}
        <button data-testid="tenant-login-submit" type="submit" disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: ACCENT }}>
          {loading && <Loader2 size={16} className="animate-spin" />}
          Se connecter
        </button>
      </form>
      <p className="mt-5 text-center text-sm text-muted-foreground">
        Pas encore de compte ?{" "}
        <button data-testid="tenant-goto-register" onClick={() => setScreen("register")} className="font-semibold text-navy underline">
          Créer mon compte
        </button>
      </p>
    </div>
  );
}

function RegisterCard({ onBack }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", logement: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.password || (!f.phone && !f.email)) {
      toast.error("Nom, mot de passe et téléphone ou email requis");
      return;
    }
    setLoading(true);
    try {
      const res = await tenantRegister(f);
      toast.success(res.message || "Demande envoyée !");
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tenant-register-card" className="mx-auto max-w-md rounded-3xl bg-white p-7 shadow-2xl">
      <div className="mb-5 text-center">
        <h1 className="font-display text-2xl font-semibold text-navy">Créer mon compte</h1>
        <p className="mt-1 text-sm text-muted-foreground">Votre demande sera validée par TIRERA GROUP</p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="tenant-reg-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Nom complet *" required />
        <input data-testid="tenant-reg-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="Téléphone (ex. 92 00 10 10)" />
        <input data-testid="tenant-reg-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="Email (optionnel)" />
        <input data-testid="tenant-reg-logement" value={f.logement} onChange={(e) => set("logement", e.target.value)} className={inputCls} placeholder="Logement que vous louez (nom / adresse)" />
        <input data-testid="tenant-reg-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className={inputCls} placeholder="Mot de passe *" required />
        <button data-testid="tenant-reg-submit" type="submit" disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: ACCENT }}>
          {loading && <Loader2 size={16} className="animate-spin" />}
          Envoyer ma demande
        </button>
      </form>
      <p className="mt-2 text-center text-xs text-muted-foreground">* Indiquez au moins un téléphone ou un email</p>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        Vous avez déjà un compte ?{" "}
        <button data-testid="tenant-goto-login" onClick={onBack} className="font-semibold text-navy underline">Se connecter</button>
      </p>
    </div>
  );
}

function Portal({ property, receipts, onLogout }) {
  const paid = property.rent_status === "a_jour";
  return (
    <div data-testid="tenant-portal" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-white/60">Espace locataire</p>
          <h1 className="font-display text-2xl font-semibold text-white">Bonjour {property.tenant || ""} 👋</h1>
        </div>
        <button data-testid="tenant-logout" onClick={onLogout}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20">
          <LogOut size={15} /> Quitter
        </button>
      </div>

      {/* Property card */}
      <div className="rounded-3xl bg-white p-6 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-navy">
              <Home size={18} /> <span className="font-display text-lg font-semibold">{property.label}</span>
            </div>
            {property.address && <p className="mt-1 text-sm text-muted-foreground">{property.address}</p>}
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${paid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
            {paid ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            {paid ? "Loyer à jour" : "Loyer impayé"}
          </span>
        </div>
        <div className="mt-4 border-t border-border pt-4">
          <div className="text-sm text-muted-foreground">Loyer mensuel</div>
          <div className="font-display text-2xl font-bold" style={{ color: ACCENT }}>{fmt(property.rent)} <span className="text-sm font-medium text-muted-foreground">FCFA / mois</span></div>
        </div>

        {!paid && (
          <button data-testid="tenant-pay-orange" onClick={() => toast.info("Le paiement en ligne par Orange Money sera bientôt disponible.")}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6600] px-6 py-3 text-sm font-semibold text-white hover:opacity-90">
            <Smartphone size={16} /> Payer mon loyer avec Orange Money
          </button>
        )}
      </div>

      {/* Receipts */}
      <div className="rounded-3xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold text-navy"><FileText size={18} /> Mes reçus de loyer</h2>
        {receipts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun reçu pour le moment. Vos reçus apparaîtront ici après chaque paiement enregistré.</p>
        ) : (
          <ul className="divide-y divide-border">
            {receipts.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <div className="font-medium text-navy">{r.period} — {fmt(r.amount)} FCFA</div>
                  <div className="text-xs text-muted-foreground">N° {r.receipt_number}</div>
                </div>
                <a href={tenantReceiptUrl(r.id)} target="_blank" rel="noopener noreferrer" data-testid={`tenant-receipt-download-${r.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white hover:opacity-90" style={{ backgroundColor: ACCENT }}>
                  <Download size={14} /> Reçu PDF
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
